import { Link } from 'react-router-dom';
import LegalShell from '../../components/legal/LegalShell';

/**
 * Cookie Policy — the public NOTICE describing the cookie categories we use and
 * how to control them. It is distinct from the interactive control at
 * `/legal/cookie-preferences` and from the broader Privacy Policy. Categories
 * are sourced from Termly's cookie scan; this static copy can later be replaced
 * by a Termly-generated embed. See `docs/privacy-consent.md`.
 */
export default function CookiePolicy() {
  return (
    <LegalShell title="Cookie Policy" version="v1.1" effective="2026-07-25">
      <p>
        This Cookie Policy explains how Andrew Paris LLC, d/b/a
        MediaShare-Link ("we," "us,"
        "our") uses cookies and similar tracking
        technologies on the OculusMind.AI website and AI-agent platform
        (collectively, the "Services"). It supplements, and should be read
        alongside, our{' '}
        <Link to="/legal/privacy">Privacy Policy</Link>.
      </p>

      <h2>What Are Cookies?</h2>
      <p>
        Cookies are small text files stored on your device when you visit a
        website. They are widely used to make websites work, to remember your
        preferences, and to provide reporting and advertising information.
        "Similar technologies" include local storage, pixels, tags, and software
        development kits that perform comparable functions.
      </p>

      <h2>How We Manage Consent</h2>
      <p>
        We use <strong>Termly</strong> as our Consent Management Platform (CMP)
        to present a consent banner, record your choices, and signal those
        choices to downstream technologies through the IAB Transparency &amp;
        Consent Framework (TCF) and Global Privacy Platform (GPP). Where
        applicable law requires it, non-essential cookies are not set until you
        consent, and you can change or withdraw your consent at any time.
      </p>

      <h2>Categories of Cookies We Use</h2>
      <ul>
        <li>
          <strong>Strictly necessary.</strong> Required for the Services to
          function — for example, authentication, session management, and
          security. These cannot be switched off through the consent controls.
        </li>
        <li>
          <strong>Functional.</strong> Remember preferences and choices (such as
          the agent you last used) to provide enhanced, more personal features.
        </li>
        <li>
          <strong>Analytics / performance.</strong> Help us understand how
          visitors interact with the Services so we can measure and improve them.
        </li>
        <li>
          <strong>Advertising.</strong> Used by us or our advertising partners to
          build a profile of your interests and show relevant advertising on the
          Services and elsewhere. We do not enable these without a valid legal
          basis or your consent.
        </li>
      </ul>

      <h2>Managing Your Choices</h2>
      <p>
        You can review and change your consent at any time through our{' '}
        <Link to="/legal/cookie-preferences">Cookie Preferences</Link> control.
        Most browsers also let you block or delete cookies through their
        settings; doing so may affect how the Services function.
      </p>

      <h2>Global Privacy Control and Opt-Out Signals</h2>
      <p>
        Where required by applicable law, we honor browser-based opt-out
        preference signals such as Global Privacy Control (GPC). When our CMP
        detects a GPC signal, it applies the corresponding opt-out for that
        browser. For additional interest-based-advertising opt-outs, see the
        "Opting Out of Interest-Based Advertising" section of our{' '}
        <Link to="/legal/privacy">Privacy Policy</Link>.
      </p>

      <h2>Changes to This Cookie Policy</h2>
      <p>
        We may update this Cookie Policy from time to time. Changes will be
        reflected by updating the effective date above.
      </p>

      <h2>Contact</h2>
      <address>
        Andrew Paris LLC, d/b/a MediaShare-Link<br />
        5900 Balcones Dr Suite 21007<br />
        Austin, TX 78731<br />
        United States
      </address>
      <p>
        Email: <a href="mailto:Help@OculusMind.AI">Help@OculusMind.AI</a>
      </p>
    </LegalShell>
  );
}
